/* ═══════════════════════════════════════════════════════
   ClientCardSection — the client-facing session card, admin side.
   ═══════════════════════════════════════════════════════
   Logistics only (where to go, who to reach, what to wear and bring, what to
   expect). Recipients are CRM contacts linked to this job, nothing else: the
   tick list below shows the job's linked contacts, and "Link another contact"
   is the same LinkedContacts control the Job Card uses. Card-only fields
   live in clientCards/{job}__{session}; job fields are read here, edited on
   their own tabs. Design: docs/crew-cards/DESIGN.md.
*/
import React, { useEffect, useMemo, useState } from "react";
import { C, font, mono, inp, lbl, btnS } from "../../../../lib/styles.js";
import { fmtDate, FMT_DATE_LONG, sortSessions } from "../../../../lib/utils.js";
import { useData } from "../../../../context/DataContext.jsx";
import { crewCardApi } from "../../../../lib/crewCardCallables.js";
import {
  crewCardId, cardSessionId, sessionForCard, blankClientCard, computeClientGaps, buildClientSnapshot,
  fingerprintOf, seedClientCard, clientRecipientsOf, crewNamesOf, rosterEntryFor, fmtPhone, secondContactOf,
  CAPTURE_SUBJECTS, CAPTURE_SUBJECT_LABELS, detectCaptureSubject, isChosenSubject, isSheetSessionEligible, lengthMinutesOf,
} from "../../../../lib/crewCardRules.js";
import ClientCardView from "../../../../crewcard/ClientCardView.jsx";
import CardStatusRail from "./CardStatusRail.jsx";
import LinkedContacts from "./LinkedContacts.jsx";

const Tag = ({ children }) => <span style={{ fontFamily: mono, fontSize: 9, letterSpacing: "0.08em", textTransform: "uppercase", padding: "1px 6px", borderRadius: 3, color: C.purple, background: `${C.purple}18` }}>{children}</span>;
const FL = ({ children, tag }) => <div style={{ ...lbl, marginBottom: 4, display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", minWidth: 0 }}>{children}{tag && <Tag>{tag}</Tag>}</div>;
const small = { ...inp, fontSize: 12, padding: "7px 10px", boxSizing: "border-box", minWidth: 0 };
const area = { ...small, resize: "vertical", lineHeight: 1.5 };
const gapStyle = { borderColor: C.amber, background: `${C.amber}10` };
const tiny = { ...btnS, padding: "3px 8px", fontSize: 10 };

/* Style: the look of the sheet. Outside the fingerprint, so choosing one
   never flags "changed" and never resends. Same small buttons as the day picker. */
const STYLE_OPTIONS = [["normcore", "Normcore"], ["cool", "Cool"]];
function StyleControl({ value, onChange }) {
  const v = value === "cool" ? "cool" : "normcore";
  return (
    <span style={{ display: "inline-flex", gap: 4 }}>
      {STYLE_OPTIONS.map(([k, label]) => (
        <button key={k} type="button" aria-pressed={v === k} onClick={() => onChange(k)}
          style={{ ...tiny, background: v === k ? C.surfaceAlt : C.surface, color: v === k ? C.text : C.muted }}>{label}</button>
      ))}
    </span>
  );
}

/* Why a session gets no Session Sheet (SHEET_SESSION_TYPES: capture days only). */
const noSheetNote = (session) => `No session sheet for a ${String(session?.sessionType || "").trim() || "capture"} day.`;

export default function ClientCardSection({ job, updateJobFields, editing }) {
  const { clientCards, crewCards, saveClientCard, flushJobSave, contacts, clients, studioSettings, savedQuotes } = useData();
  const rosterStored = studioSettings?.crewRoster;
  const roster = useMemo(() => rosterStored || [], [rosterStored]);

  const sessions = useMemo(() => sortSessions(job?.sessions || []), [job?.sessions]);
  const fallbackSid = cardSessionId(job);
  const [sid, setSid] = useState(() => cardSessionId(job));
  useEffect(() => { if (!sessions.some(s => s.id === sid) && sid !== "root") setSid(fallbackSid); }, [sessions, sid, fallbackSid]);
  const session = useMemo(() => sessionForCard(job, sid), [job, sid]);
  const quote = useMemo(() => (savedQuotes || []).find(q => q.id === job?.quoteId) || null, [savedQuotes, job?.quoteId]);
  const crew = crewNamesOf(job);

  const stored = useMemo(() => (clientCards || []).find(c => c.id === crewCardId(job.id, sid)) || null, [clientCards, job.id, sid]);
  /* The Call Sheet's card for the same session, {} until the Suite makes one:
     the point person must be crew marked on site there, and the second
     contact's On site / Off site label reads the same presence. The server
     passes it too, so the gaps and the fingerprint the Reviewed tick stores
     match; without it a second contact who is off site by role would hash
     on site here and off site there, and the tick would be stale at once. */
  const crewCard = useMemo(() => (crewCards || []).find(c => c.id === crewCardId(job.id, sid)) || {}, [crewCards, job.id, sid]);
  const card = useMemo(() => {
    if (stored) return stored;
    const seeds = seedClientCard({ job, session, quote, pointPerson: "" });
    return { ...blankClientCard(job.id, sid), ...seeds, _seeded: true };
  }, [stored, job, sid, session, quote]);
  /* The subject this form works with. Persisted on EVERY save (below) so the
     server, which only validates what the card carries, fingerprints the same
     value the form did — otherwise a card seeded before the field existed, or
     one whose quote changed after its first save, reads "Changed since v1"
     forever while Resend answers "nothing has changed". */
  const subject = isChosenSubject(card.subject) ? card.subject : detectCaptureSubject({ job, quote });
  const patchCard = (p) => {
    const { _seeded, ...rest } = card;
    void _seeded;
    return saveClientCard({ ...rest, subject, ...p });
  };
  const seedsFor = (pointPerson, subj) => seedClientCard({ job, session, quote, pointPerson, subject: subj });
  /* The arrival line names the on-site point person, but it is seeded before
     one is chosen — atlpow_260909 shipped "your on-site contact will meet you
     there" to the client. The seed no longer fills that blank, and picking a
     person now rewrites the line, unless the line has been typed over. */
  const setPointPerson = (name) => {
    const untouched = !card.arrival || card.arrival === seedsFor(card.pointPerson, subject).arrival;
    patchCard({ pointPerson: name, ...(untouched ? { arrival: seedsFor(name, subject).arrival } : {}) });
  };
  const setSubject = (next) => {
    const seeded = seedsFor(card.pointPerson, subject);
    const fresh = seedsFor(card.pointPerson, next);
    // Only rewrite the texts the admin has not made their own. A field the
    // admin blanked on purpose is theirs too, so this is not a truthiness test.
    const keep = (field) => (typeof card[field] === "string" && card[field] !== seeded[field] ? card[field] : fresh[field]);
    patchCard({ subject: next, arrival: keep("arrival"), capturing: keep("capturing"), bring: keep("bring"), expect: keep("expect") });
  };
  // Length: an integer 1..1440 or null (= call to wrap). An empty box clears it.
  const setLength = (raw) => {
    const v = String(raw ?? "").trim();
    patchCard({ lengthMinutes: v === "" ? null : (lengthMinutesOf(v) || null) });
  };

  const contactsSorted = useMemo(() => [...(contacts || [])].sort((a, b) => String(a?.name || "").localeCompare(String(b?.name || ""))), [contacts]);
  const recipients = useMemo(() => clientRecipientsOf(job, contacts, card), [job, contacts, card]);
  // The card as every save writes it (with the subject), so the gaps' review
  // check and the fingerprint the Reviewed tick stores see the same sheet.
  const cardAsSaved = useMemo(() => ({ ...card, subject }), [card, subject]);
  const { gaps, required, filled } = useMemo(() => computeClientGaps({ job, session, card: cardAsSaved, roster, contacts, crewCard }), [job, session, cardAsSaved, roster, contacts, crewCard]);
  const snapshot = useMemo(() => buildClientSnapshot({ job, session, card: cardAsSaved, roster, crewCard }), [job, session, cardAsSaved, roster, crewCard]);
  const onLocation = !session?.isStudio;
  // The same labels the sheet prints (labelsFor in ClientCardView).
  const bringLabel = onLocation ? "What we need on site" : subject === "people" ? "For the talent" : "What to bring";
  const eligible = isSheetSessionEligible("client", session);
  const fp = useMemo(() => fingerprintOf(snapshot), [snapshot]);
  const last = card.lastDeploy;
  const state = !last || last.revokedAt ? "none" : last.fingerprint === fp ? "sent" : "changed";
  const stateLabel = state === "none" ? (last?.revokedAt ? "Link revoked" : "Draft, not sent") : state === "sent" ? `Sent v${last.version}` : `Changed since v${last.version}`;
  const gapKeys = new Set(gaps.map(g => g.key));
  /* Reviewed covers the sheet as it was when the box was ticked (the tick
     stores that sheet's fingerprint). Ticked but still a gap means the sheet
     changed since: the box shows unticked until someone reads it again. */
  const reviewStale = card.reviewed === true && gapKeys.has("reviewed");
  const setReviewed = (on) => patchCard(on ? { reviewed: true, reviewedFp: fp } : { reviewed: false, reviewedFp: "" });

  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [preview, setPreview] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [linking, setLinking] = useState(false);

  const deploy = async () => {
    if (gaps.length || busy) return;
    setBusy(true); setMsg(null);
    try {
      const [saved, jobSaved] = await Promise.all([patchCard({}), flushJobSave ? flushJobSave(job.id) : Promise.resolve(true)]);
      if (!saved) throw new Error("the sheet did not save");
      if (!jobSaved) throw new Error("the job did not save");
      const r = await crewCardApi.deploy({ jobDocId: job.id, sessionId: sid, kind: "client" });
      // warning: something the admin should know (several unrecorded earlier links, a new one sent).
      const warn = r.warning ? ` ${r.warning}` : "";
      if (r.unchanged) setMsg({ ok: true, warn: !!r.warning, text: `Already sent as v${r.version}; nothing has changed since.${warn}` });
      else setMsg({ ok: true, warn: !!r.warning, text: `Sent v${r.version} to ${r.sent} of ${r.sent + r.failed.length}${r.failed.length ? `. Failed: ${r.failed.join(", ")}` : "."}${warn}` });
    } catch (e) {
      const g = e?.details?.gaps;
      setMsg({ ok: false, text: g?.length ? `Not sent. Missing: ${g.map(x => x.label).join("; ")}` : `Not sent: ${e.message}` });
    }
    setBusy(false);
  };
  const revoke = async () => {
    if (!last?.token || busy) return;
    setBusy(true); setMsg(null); setConfirmRevoke(false);
    try { await crewCardApi.revoke({ token: last.token }); setMsg({ ok: true, text: "Link revoked." }); }
    catch (e) { setMsg({ ok: false, text: `Could not revoke: ${e.message}` }); }
    setBusy(false);
  };
  const copyLink = async () => {
    if (!last?.url) return;
    if (!navigator?.clipboard?.writeText) { setMsg({ ok: false, text: `Copy failed. The link is ${last.url}` }); return; }
    try { await navigator.clipboard.writeText(last.url); setMsg({ ok: true, text: "Link copied." }); }
    catch { setMsg({ ok: false, text: `Copy failed. The link is ${last.url}` }); }
  };

  // Recipient tick list: every contact linked to the job. Ticking writes an
  // explicit recipientIds list (an empty one means nobody, which blocks
  // sending); null means the default rule: contacts and producers.
  const linked = (job?.linkedContacts || []).map(l => ({ ...l, contact: (contacts || []).find(c => c.id === l.contactId) })).filter(l => l.contact);
  const picked = new Set(Array.isArray(card.recipientIds) ? card.recipientIds : linked.filter(l => l.role === "contact" || l.role === "producer").map(l => l.contactId));
  const toggleRecipient = (contactId) => {
    const next = new Set(picked);
    if (next.has(contactId)) next.delete(contactId); else next.add(contactId);
    patchCard({ recipientIds: [...next] });
  };
  const statusFor = (email) => (card.recipientStatus || []).find(s => (s.email || "").toLowerCase() === email) || null;
  const fmtStamp = (iso) => iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "";
  const rows = recipients.map(rcp => {
    const st = statusFor(rcp.email);
    const dl = st?.delivery;
    return [
      { text: rcp.name },
      { text: rcp.email, mono: true },
      { text: st ? `${dl || "sent"}${st.sentAt ? " " + fmtStamp(st.sentAt) : ""}${dl === "bounced" || dl === "failed" ? " · " + st.email : ""}` : "—", mono: true, color: dl === "bounced" || dl === "failed" ? C.danger : dl === "delivered" ? C.green : C.muted },
      { text: st ? (st.openedAt ? fmtStamp(st.openedAt) : "not yet") : "—", mono: true, italic: !st?.openedAt, color: st?.openedAt ? C.text : C.muted },
    ];
  });
  const point = rosterEntryFor(roster, card.pointPerson);
  /* Second contact: the select shows the effective one (the default rule
     applies until the admin picks); the helper line reads it back from the
     snapshot. A name missing from the roster (renamed or removed) is still
     offered, so the select never shows "None" for a name the sheet prints. */
  const secondName = secondContactOf({ job, card });
  const second = snapshot.second;
  const rosterNames = [...new Set(roster.map(r => String(r?.name || "").trim()).filter(Boolean))];
  const secondValue = rosterNames.find(n => n.toLowerCase() === secondName.toLowerCase()) || secondName;
  const secondOptions = secondValue && !rosterNames.includes(secondValue) ? [...rosterNames, secondValue] : rosterNames;

  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: 6, background: C.surface, fontFamily: font }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderBottom: `1px solid ${C.border}`, flexWrap: "wrap" }}>
        <span style={{ fontSize: 13, fontWeight: 500 }}>Session Sheet{session?.date ? ` · ${fmtDate(session.date, FMT_DATE_LONG)}` : ""}</span>
        <span style={{ fontSize: 11, color: C.muted }}>Where to go, who to reach, what to bring. No money, no crew list.</span>
        {job?.multiDay && sessions.length > 1 && (
          <span style={{ display: "flex", gap: 4, marginLeft: "auto" }}>
            {sessions.map(s => (
              <button key={s.id} type="button" onClick={() => setSid(s.id)} style={{ ...tiny, background: s.id === sid ? C.surfaceAlt : C.surface }}>{fmtDate(s.date) || "Undated"}</button>
            ))}
          </span>
        )}
        {eligible && (
          <span style={{ flexBasis: "100%", display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap", fontSize: 11, color: C.muted }}>
            <span style={{ ...lbl, marginBottom: 0 }}>Style</span>
            <StyleControl value={card.style} onChange={style => patchCard({ style })} />
            <Tag>card</Tag>
            <span>Look of the sheet. Changing it never resends.</span>
          </span>
        )}
      </div>

      {!eligible && (
        <div style={{ padding: "12px 14px", fontSize: 12, color: C.muted }}>{noSheetNote(session)}</div>
      )}

      {eligible && <div className="ccs-layout" style={{ padding: 12 }}>
        <div className="ccs-main" style={{ display: "grid", gap: 12, minWidth: 0 }}>
          <div className="ccs-grid3" style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "8px 14px" }}>
            <div><FL>When</FL><div style={{ ...small, background: C.surfaceAlt, color: gapKeys.has("date") || gapKeys.has("callTime") ? C.amber : C.muted }}>{[snapshot.date ? fmtDate(snapshot.date, FMT_DATE_LONG) : "No date", snapshot.callTime ? `arrive ${snapshot.callTime}` : "no call time", snapshot.duration].filter(Boolean).join(" · ")}</div></div>
            <div><FL>Where</FL><div style={{ ...small, background: C.surfaceAlt, color: gapKeys.has("location") ? C.amber : C.muted }}>{snapshot.location.line || "No location. Set it on the Job Card."}</div></div>
            <div>
              <FL tag="card">Length (minutes)</FL>
              <input type="number" inputMode="numeric" min={1} max={1440} step={1} value={card.lengthMinutes ?? ""} onChange={e => setLength(e.target.value)} style={small} placeholder={snapshot.wrapTime ? "Call to wrap" : "Optional"} />
              <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>Overrides call to wrap. Empty = call to wrap.</div>
            </div>
            <div>
              <FL tag="card">On-site contact</FL>
              <select value={card.pointPerson || ""} onChange={e => setPointPerson(e.target.value)} style={{ ...small, ...(gapKeys.has("pointPerson") || gapKeys.has("pointPhone") ? gapStyle : {}) }}>
                <option value="">Choose from the crew</option>{crew.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              <div style={{ fontSize: 11, color: point?.phone ? C.muted : C.amber, marginTop: 4 }}>{card.pointPerson ? (point?.phone ? `Phone ${fmtPhone(point.phone)}` : "No phone on the roster for this person (Job Prep Settings)") : "Their number goes on the sheet."}</div>
            </div>
            <div>
              <FL tag="card">Second contact</FL>
              <select value={secondValue} onChange={e => patchCard({ secondContact: e.target.value })} style={{ ...small, ...(gapKeys.has("secondPhone") ? gapStyle : {}) }}>
                <option value="">None</option>{secondOptions.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              <div style={{ fontSize: 11, color: second.name && !second.phone ? C.amber : C.muted, marginTop: 4 }}>{second.name ? `${second.onSite ? "On site" : "Off site"} · ${second.phone || "no phone on the roster"}` : "No second contact on the sheet."}</div>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", fontSize: 12, color: C.muted }}>
            <span style={{ ...lbl, marginBottom: 0 }}>What is being captured</span>
            <select value={subject} onChange={e => setSubject(e.target.value)} style={{ ...small, width: "auto", minWidth: 130 }}>
              <option value="">Not sure</option>
              {CAPTURE_SUBJECTS.map(x => <option key={x} value={x}>{CAPTURE_SUBJECT_LABELS[x]}</option>)}
            </select>
            <span>{onLocation ? "On location: the sheet is written to a client whose site we are coming to." : "At the studio: the sheet is written to a client coming to us."} Changing this rewrites any of the four texts below you have not edited.</span>
          </div>
          <div>
            <FL tag="card">{onLocation ? "Arrival and access" : "Arrival and parking"}</FL>
            <textarea value={card.arrival || ""} onChange={e => patchCard({ arrival: e.target.value })} rows={3} style={{ ...area, ...(gapKeys.has("arrival") ? gapStyle : {}) }} placeholder={onLocation ? "Address, entrance, who meets our crew, what needs to be open" : "Address, entrance, parking, who meets them"} />
          </div>
          <div>
            <FL tag="card">What we're capturing</FL>
            <textarea value={card.capturing || ""} onChange={e => patchCard({ capturing: e.target.value })} rows={3} style={area} placeholder="One line per item, in the client's words: what is captured and with what (camera array, handheld scanner), never a rig name" />
            <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>One line per item. Seeded from the quote's catalog lines; a custom line is left for you to write.</div>
          </div>
          <div>
            <FL tag="card">{bringLabel}</FL>
            <textarea value={card.bring || ""} onChange={e => patchCard({ bring: e.target.value })} rows={3} style={area} placeholder={onLocation ? "Access, clearance, anything we will not be able to walk up to" : subject === "objects" ? "What the client brings, and how it should be handled" : "Wardrobe, hair, anything they should bring or avoid"} />
          </div>
          <div>
            <FL tag="card">What to expect</FL>
            <textarea value={card.expect || ""} onChange={e => patchCard({ expect: e.target.value })} rows={3} style={area} placeholder="What the capture is like, how long it takes, who can come along" />
          </div>
          <div style={{ fontSize: 11, color: C.muted }}>
            Afterwards, from the job: {[snapshot.deliverable.format, snapshot.deliverable.turnaround ? `turnaround ${snapshot.deliverable.turnaround}` : ""].filter(Boolean).join(" · ") || "no deliverable text yet (Delivery tab)"}.
            <button type="button" style={{ ...btnS, padding: "2px 8px", fontSize: 10, marginLeft: 8 }} onClick={() => patchCard(seedsFor(card.pointPerson, subject))}>Reseed the four texts</button>
          </div>

          <div>
            <FL tag="card">Recipients</FL>
            {linked.length === 0 && <div style={{ fontSize: 12, color: C.amber }}>No contacts linked to this job yet. Link one below; the person must already be a contact.</div>}
            {linked.map(l => {
              const email = ((l.contact.emails || [])[0] || l.contact.email || "").toLowerCase();
              return (
                <label key={l.contactId} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, padding: "3px 0", cursor: "pointer" }}>
                  <input type="checkbox" checked={picked.has(l.contactId) && !!email} disabled={!email} onChange={() => toggleRecipient(l.contactId)} style={{ accentColor: C.purple }} />
                  <span>{l.contact.name}</span>
                  <span style={{ color: C.muted }}>{l.role}</span>
                  <span style={{ fontFamily: mono, fontSize: 11, color: email ? C.muted : C.amber }}>{email || "no email on the contact"}</span>
                </label>
              );
            })}
            <button type="button" style={{ ...btnS, padding: "4px 10px", fontSize: 10, marginTop: 6 }} onClick={() => setLinking(v => !v)}>{linking ? "Done linking" : "Link another contact"}</button>
            {linking && (
              <div style={{ marginTop: 8, padding: 10, border: `1px solid ${C.border}`, borderRadius: 6 }}>
                <LinkedContacts job={job} contacts={contacts} contactsSorted={contactsSorted} clients={clients} editing={editing !== false} onUpdate={updateJobFields} />
              </div>
            )}
          </div>

          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, padding: "6px 10px", border: `1px solid ${gapKeys.has("reviewed") ? C.amber : C.border}`, borderRadius: 6, cursor: "pointer" }}>
            <input type="checkbox" checked={card.reviewed === true && !reviewStale} onChange={e => setReviewed(e.target.checked)} style={{ accentColor: C.green }} />
            <span><b>Reviewed.</b> I have read this sheet as the client will see it. Nothing goes out to a client until this is ticked.{reviewStale && <span style={{ color: C.amber }}> Changed since it was reviewed: tick Reviewed again.</span>}</span>
          </label>
        </div>

        <CardStatusRail
          chip={{ state, label: stateLabel }} filled={filled} required={required} gaps={gaps}
          note={state === "none" && !gaps.length ? "Auto-sends at 4:00 PM the day before." : null}
          msg={msg} busy={busy}
          onPreview={() => setPreview(true)} onCopy={copyLink} canCopy={!!last?.url && !last?.revokedAt}
          onRevoke={last?.token && !last?.revokedAt ? () => setConfirmRevoke(true) : null}
          onDeploy={deploy} deployLabel={state === "none" ? "Send to client" : "Resend"} deployDisabled={!!gaps.length}
          confirmRevoke={confirmRevoke} onConfirmRevoke={revoke} onCancelRevoke={() => setConfirmRevoke(false)}
          columns={["Recipient", "Email", "Sent", "Opened"]} rows={rows}
          footer={last?.url && !last?.revokedAt ? <>Link: <span style={{ fontFamily: mono }}>{last.url}</span> · expires 3 days after the session. The client can forward the sheet to the person being scanned.</> : "Recipients are contacts linked to this job. Each gets a personal link; the sheet has a share button for forwarding."}
        />
      </div>}

      {preview && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(40,40,42,0.92)", display: "flex", flexDirection: "column" }} onClick={() => setPreview(false)}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 20px", borderBottom: "1px solid rgba(255,255,255,0.12)" }} onClick={e => e.stopPropagation()}>
            <span style={{ color: "#ddd", fontSize: 13 }}>Preview · what the client will see{gaps.length ? ` · ${gaps.length} gap${gaps.length === 1 ? "" : "s"} still open` : ""}</span>
            <button type="button" style={btnS} onClick={() => setPreview(false)}>← Back</button>
          </div>
          <div style={{ flex: 1, overflow: "auto", padding: "24px 12px" }} onClick={e => e.stopPropagation()}>
            {/* 600px wide: over the sheet's 560px phone breakpoint (container width), so the page layout shows. */}
            <div style={{ maxWidth: 600, margin: "0 auto", borderRadius: 6, overflow: "hidden", boxShadow: "0 8px 60px rgba(0,0,0,0.5)" }}>
              <ClientCardView snap={snapshot} preview version={last?.version || 0} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
